/**
 * @fileoverview Re-fetches the signed-in user's capabilities.
 * @description A Super Admin can change role capabilities at any time, so what this tab holds for
 * the current user (menu, routes, actions — all derived from GET /identity/me plus GET
 * /authorization/me/capabilities) may be out of date. refreshSessionAccess marks that query stale
 * and re-fetches it if anything is showing it, so the screen re-renders with the corrected access.
 * It lives in shared (not features/auth) because the response interceptor calls it on a `403 /
 * 2008`, and shared may not import a feature (ADR-0002). `cancelRefetch: false` joins an in-flight
 * fetch instead of restarting it, so several parallel 403s (or a focus and a navigation landing
 * together) cost one round of requests. refreshStaleSessionAccess is the throttled variant, for
 * triggers that fire often.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { CURRENT_SESSION_QUERY_KEY } from '@/shared/constants/session-query-keys.constants'
import { queryClient } from '@/shared/lib/query-client'

/**
 * @description Re-fetches the current user's principal and capabilities (no-op beyond marking it
 * stale when nothing is observing it, e.g. on the login page).
 * @returns {Promise<void>} Resolves once the re-fetch settles; never rejects, since a failed
 * background refresh keeps showing the last known access.
 */
export async function refreshSessionAccess(): Promise<void> {
  try {
    await queryClient.invalidateQueries(
      { queryKey: CURRENT_SESSION_QUERY_KEY },
      { cancelRefetch: false },
    )
  } catch {
    // A failed refresh is not actionable here; the last known access stays on screen.
  }
}

/**
 * @description Re-fetches the current user's principal and capabilities only if they are stale
 * (older than the query's own staleTime) and something is showing them. Used for the page-navigation
 * trigger, so clicking around costs at most one round of requests per staleTime instead of one per
 * click; the tab-focus trigger follows the same staleTime, and a `403 / 2008` always re-fetches
 * (refreshSessionAccess).
 * @returns {Promise<void>} Resolves once the re-fetch (if any) settles; never rejects.
 */
export async function refreshStaleSessionAccess(): Promise<void> {
  try {
    await queryClient.refetchQueries(
      { queryKey: CURRENT_SESSION_QUERY_KEY, type: 'active', stale: true },
      { cancelRefetch: false },
    )
  } catch {
    // A failed refresh is not actionable here; the last known access stays on screen.
  }
}
